import { pickT } from "@repo/shared";
import type { FlowInstanceStatus, Translated } from "@repo/shared/types";
import { Ban, Check, FileText, Landmark, PenLine, Send, ShieldCheck, Trophy } from "lucide-react";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./process-stepper.i18n";

type ProcessStepT = Translated<typeof T>;

const STEP_ICONS: Partial<Record<FlowInstanceStatus, typeof Send>> = {
  submitted: Send,
  pdf_generated: FileText,
  compliance_review: ShieldCheck,
  external_review: Landmark,
  signing: PenLine,
  completed: Trophy,
};

function stepLabel(t: ProcessStepT, step: string): string {
  const labels: Partial<Record<string, string>> = t;
  return labels[step] || step.replaceAll("_", " ");
}

export function getProcessStepLabel(step: string, lang: string): string {
  return stepLabel(pickT(T, lang), step);
}

type ProcessStepperProps = {
  steps: FlowInstanceStatus[];
  /** Current instance status. "draft"/"cancelled" mean no step is active. */
  status: FlowInstanceStatus;
};

/**
 * Horizontal process stepper. Steps before the current one render as done,
 * the current one as active, later ones grayed out. When the instance is
 * cancelled the whole strip renders muted with a cancelled marker.
 */
export function ProcessStepper({ steps, status }: ProcessStepperProps) {
  const t = useT(T);
  const currentIndex = steps.indexOf(status);
  const isCancelled = status === "cancelled";

  return (
    <div className="flex items-center w-full" data-testid="process-stepper">
      {steps.map((step, index) => {
        const Icon = STEP_ICONS[step] ?? Check;
        const isDone = !isCancelled && (currentIndex > index || status === "completed");
        const isActive = !isCancelled && currentIndex === index && status !== "completed";

        return (
          <div key={step} className={cn("flex items-center", index > 0 && "flex-1")}>
            {index > 0 && (
              <div
                className={cn(
                  "h-0.5 flex-1 mx-2 rounded",
                  isDone || isActive ? "bg-primary" : "bg-border",
                )}
              />
            )}
            <div className="flex flex-col items-center gap-1.5 min-w-20" data-step={step}>
              <div
                className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors",
                  isDone && "border-primary bg-primary text-primary-foreground",
                  isActive && "border-primary bg-primary/10 text-primary",
                  !isDone && !isActive && "border-border bg-muted text-muted-foreground/50",
                )}
              >
                {isDone ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
              </div>
              <span
                className={cn(
                  "text-xs font-medium text-center",
                  isActive && "text-primary",
                  isDone && "text-foreground",
                  !isDone && !isActive && "text-muted-foreground/60",
                )}
              >
                {stepLabel(t, step)}
              </span>
            </div>
          </div>
        );
      })}
      {isCancelled && (
        <div className="flex items-center gap-2 ml-4 text-destructive">
          <Ban className="h-5 w-5" />
          <span className="text-sm font-medium">{t.cancelled}</span>
        </div>
      )}
    </div>
  );
}
